'use client';

import { forwardRef, useEffect, useState } from 'react';
import type { ReceiptSettings, PaperStyle } from '@/lib/templates/schema';
import type { ReactNode } from 'react';
import { normalizeFont } from '@/lib/templates/defaults';
import { useWatermarkRequired } from './watermark-context';

interface ReceiptPaperProps {
  settings: ReceiptSettings;
  children: ReactNode;
  className?: string;
}

// Static texture image paths (stored in public/textures/)
const STATIC_TEXTURE_PATHS: Partial<Record<PaperStyle, string>> = {
  new: '/textures/new.jpg',
  ruffled: '/textures/ruffled.jpg',
  used: '/textures/used.jpg',
  worn: '/textures/worn.jpg',
  old: '/textures/old.png',
};

// Generate thermal paper texture using canvas
function generateThermalTexture(size: number = 200): string {
  if (typeof window === 'undefined') return '';
  
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';

  ctx.fillStyle = '#fafaf8';
  ctx.fillRect(0, 0, size, size);

  const imageData = ctx.getImageData(0, 0, size, size);
  const data = imageData.data;

  for (let i = 0; i < data.length; i += 4) {
    const y = Math.floor((i / 4) / size);
    const bandNoise = (y % 3 === 0) ? -2 : 0;
    const grain = (Math.random() - 0.5) * 8;
    const adjustment = bandNoise + grain;
    
    data[i] = Math.max(0, Math.min(255, data[i] + adjustment));
    data[i + 1] = Math.max(0, Math.min(255, data[i + 1] + adjustment));
    data[i + 2] = Math.max(0, Math.min(255, data[i + 2] + adjustment));
  }

  ctx.putImageData(imageData, 0, 0);
  return canvas.toDataURL('image/png');
}

export const ReceiptPaper = forwardRef<HTMLDivElement, ReceiptPaperProps>(
  function ReceiptPaper({ settings, children, className = '' }, ref) {
  // Forced ON for non-Pro users in the builder (free plan exports carry the
  // watermark); template thumbnails/admin previews keep the default (false).
  const watermarkRequired = useWatermarkRequired();
  const showWatermark = watermarkRequired || settings.showWatermark;
  const [textureUrl, setTextureUrl] = useState<string | null>(null);

  useEffect(() => {
    const { paperStyle } = settings;
    
    if (paperStyle === 'plain') {
      setTextureUrl(null);
      return;
    }
    
    const staticPath = STATIC_TEXTURE_PATHS[paperStyle];
    if (staticPath) {
      setTextureUrl(staticPath);
      return;
    }
    
    if (paperStyle === 'thermal') {
      const texture = generateThermalTexture();
      setTextureUrl(texture);
    }
  }, [settings.paperStyle]);

  return (
    <div
      ref={ref}
      className={`receipt-paper relative overflow-hidden ${className}`}
      style={{
        width: settings.width,
        backgroundColor: settings.backgroundColor,
        backgroundImage: textureUrl ? `url("${textureUrl}")` : undefined,
        backgroundRepeat: textureUrl ? 'repeat' : undefined,
        backgroundBlendMode: textureUrl ? 'multiply' : undefined,
        color: settings.textColor,
        fontFamily: normalizeFont(settings.font),
        fontSize: settings.fontSize,
        padding: '16px 16px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.05)',
      }}
    >
      {/* Watermark */}
      {showWatermark && (
        <div
          className="pointer-events-none absolute inset-0 overflow-hidden select-none"
          style={{ zIndex: 0 }}
        >
          <div
            className="absolute whitespace-nowrap opacity-[0.08]"
            style={{
              fontSize: 14,
              fontFamily: 'Arial, sans-serif',
              fontWeight: 'bold',
              color: settings.textColor,
              transform: 'rotate(-45deg)',
              transformOrigin: 'center center',
              width: '200%',
              height: '200%',
              left: '-50%',
              top: '-50%',
              display: 'flex',
              flexWrap: 'wrap',
              gap: '30px',
              justifyContent: 'center',
              alignContent: 'center',
            }}
          >
            {Array.from({ length: 100 }).map((_, i) => (
              <span key={i} className="px-4 py-2">
                TryReceiptMaker.com
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Receipt Content */}
      <div className="relative" style={{ zIndex: 1 }}>
        {children}
      </div>
    </div>
  );
  }
);
